import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/auth-context';
import apiService from '../../services/apiService';
import { useScenarios } from '../../hooks/useScenarios';
import { confirmNavigation } from '../../utils/navigationGuard';
import { getDisabledSections, isSectionKeyHidden, isPageKeyHidden } from '../../utils/projectSections';
import useIsMobileNav from '../../hooks/useIsMobileNav';
import SlotMachineModal from '../UIElements/SlotMachineModal';
import Icon from '../UIElements/Icon';
import './styling/ProjectSidebar.css';

const ProjectSidebar = ({ isVisible, projectName, embedded = false, onNavigate }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobileNav();
  const [isPinned, setIsPinned] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [scenarioDropdownOpen, setScenarioDropdownOpen] = useState(false);
  const [closeTimeout, setCloseTimeout] = useState(null);
  const [sidebarCloseTimeout, setSidebarCloseTimeout] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({
    overview: false,
    economic: false,
    engineering: true, // Default expanded
    sitewater: false,
    analysis: false,
    compliance: false,
    operation: false
  });

  // Easter egg: 10 rapid clicks on the sidebar title opens the slot machine.
  // The count lives in a ref so counting clicks doesn't re-render the sidebar.
  const [showSlotMachine, setShowSlotMachine] = useState(false);
  const easterEggCountRef = useRef(0);
  const easterEggTimeoutRef = useRef(null);

  const handleTitleClick = useCallback(() => {
    if (easterEggTimeoutRef.current) clearTimeout(easterEggTimeoutRef.current);
    easterEggCountRef.current += 1;
    if (easterEggCountRef.current >= 10) {
      easterEggCountRef.current = 0;
      setShowSlotMachine(true);
      return;
    }
    // Pausing longer than 800ms between clicks restarts the streak
    easterEggTimeoutRef.current = setTimeout(() => { easterEggCountRef.current = 0; }, 800);
  }, []);

  // Real data from API
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  // Bumped when an admin changes the project's enabled sections so the
  // freshly saved project document is refetched
  const [projectsRefreshKey, setProjectsRefreshKey] = useState(0);
  // Current user's allow permissions for the selected project
  const [userProjectPermissions, setUserProjectPermissions] = useState([]);
  
  // Use shared scenarios hook to prevent duplicate API calls
  const projectId = auth.selectedProject?._id || auth.selectedProject?.id;
  const { scenarios, loading: loadingScenarios } = useScenarios(projectId, auth.token);
  // Archived scenarios stay accessible from the project overview, but are
  // hidden from the sidebar switcher
  const visibleScenarios = scenarios.filter(s => s.status !== 'Archived');

  // Keep a ref to the latest token so effects don't re-fire on every token refresh
  const tokenRef = useRef(auth.token);
  useEffect(() => { tokenRef.current = auth.token; }); // sync on every render

  const sidebarVisible = isPinned || isHovered;

  // Close on navigation. When embedded the drawer owns dismissal; otherwise this
  // is the sidebar's own mobile overlay.
  const closeMobileSidebar = () => {
    if (embedded) onNavigate?.();
  };

  // Fetch current user's project-scoped permissions when selected project changes
  useEffect(() => {
    const fetchUserProjectPermissions = async () => {
      const token = tokenRef.current;
      if (!projectId || !token || !auth.UID) return;
      // Admins get full access without a membership check
      if (auth.user?.role === 'admin') {
        setUserProjectPermissions(['*']);
        return;
      }
      try {
        const members = await apiService.get(`/v1/project/${projectId}/members`, token);
        const membersArray = Array.isArray(members) ? members : (members.members || []);
        const myMembership = membersArray.find(m => m.userId === auth.UID);
        setUserProjectPermissions(myMembership?.permissions?.allow || []);
      } catch {
        setUserProjectPermissions([]);
      }
    };
    fetchUserProjectPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, auth.UID, auth.user?.role]); // token accessed via ref

  const hasProjectPermission = (perm) =>
    userProjectPermissions.includes('*') || userProjectPermissions.includes(perm);

  // Fetch projects on mount and whenever the selected project changes
  // (e.g. after a project is created elsewhere the list should refresh)
  useEffect(() => {
    const fetchProjects = async () => {
      const token = tokenRef.current;
      if (!token) return;
      
      try {
        setLoadingProjects(true);
        const data = await apiService.get('/v1/project', token);
        const projectsArray = Array.isArray(data) ? data : (data.projects || []);
        setProjects(projectsArray);

        // Self-heal the persisted selected project: disabledSections may have
        // been changed by an admin since it was stored in localStorage, and the
        // route guard in App.js reads it from auth.selectedProject.
        const fresh = projectsArray.find(p => (p._id || p.id) === projectId);
        if (fresh && auth.selectedProject) {
          const freshSections = getDisabledSections(fresh);
          const storedSections = getDisabledSections(auth.selectedProject);
          if (JSON.stringify([...freshSections].sort()) !== JSON.stringify([...storedSections].sort())) {
            auth.setSelectedProject(prev => (prev ? { ...prev, disabledSections: freshSections } : prev));
          }
        }
      } catch (err) {
        console.error('Error fetching projects for sidebar:', err);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectsRefreshKey]); // re-fetch when selected project changes; token accessed via ref

  // Refetch when an admin saves section toggles (dispatched by ProjectDetailsPage)
  useEffect(() => {
    const onSectionsChanged = () => setProjectsRefreshKey(k => k + 1);
    window.addEventListener('projectSectionsChanged', onSectionsChanged);
    return () => window.removeEventListener('projectSectionsChanged', onSectionsChanged);
  }, []);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const togglePin = () => {
    setIsPinned(!isPinned);
    // Notify parent component about pin state change
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('sidebarPinChanged', { detail: { isPinned: !isPinned } });
      window.dispatchEvent(event);
    }
  };

  const handleSidebarMouseEnter = () => {
    if (sidebarCloseTimeout) {
      clearTimeout(sidebarCloseTimeout);
      setSidebarCloseTimeout(null);
    }
    setIsHovered(true);
  };

  const handleSidebarMouseLeave = () => {
    if (!isPinned) {
      const timeout = setTimeout(() => {
        setIsHovered(false);
      }, 300); // 300ms delay for sidebar as well
      setSidebarCloseTimeout(timeout);
    }
  };

  const handleTriggerMouseEnter = () => {
    if (sidebarCloseTimeout) {
      clearTimeout(sidebarCloseTimeout);
      setSidebarCloseTimeout(null);
    }
    setIsHovered(true);
  };

  const toggleProjectDropdown = () => {
    setProjectDropdownOpen(!projectDropdownOpen);
    setScenarioDropdownOpen(false);
  };

  const toggleScenarioDropdown = () => {
    setScenarioDropdownOpen(!scenarioDropdownOpen);
    setProjectDropdownOpen(false);
  };

  const closeDropdowns = () => {
    setProjectDropdownOpen(false);
    setScenarioDropdownOpen(false);
  };

  const handleSelectorMouseEnter = () => {
    // Clear any pending close timeout when mouse enters
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
  };

  const handleSelectorMouseLeave = () => {
    // Set a timeout to close dropdowns with a small delay
    const timeout = setTimeout(() => {
      closeDropdowns();
    }, 300); // 300ms delay gives users time to move between button and dropdown
    setCloseTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
      if (sidebarCloseTimeout) {
        clearTimeout(sidebarCloseTimeout);
      }
      if (easterEggTimeoutRef.current) {
        clearTimeout(easterEggTimeoutRef.current);
      }
    };
  }, [closeTimeout, sidebarCloseTimeout]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectDropdownOpen || scenarioDropdownOpen) {
        const target = event.target;
        if (!target.closest('.sidebar-selector')) {
          closeDropdowns();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [projectDropdownOpen, scenarioDropdownOpen]);

  const handleProjectSelect = (project) => {
    // Blocked while the active page has unsaved changes and the user declines
    if (!confirmNavigation()) { setProjectDropdownOpen(false); return; }
    auth.setSelectedProject(project);
    auth.setSelectedScenario(null);
    setProjectDropdownOpen(false);
    navigate('/project-details');
  };

  const handleScenarioSelect = (scenario) => {
    // Re-selecting the current scenario is a no-op — don't prompt or churn context
    const currentId = auth.selectedScenario?._id || auth.selectedScenario?.id;
    if ((scenario._id || scenario.id) === currentId) { setScenarioDropdownOpen(false); return; }
    // Switching scenario reloads the page's data — same data-loss risk as navigating
    if (!confirmNavigation()) { setScenarioDropdownOpen(false); return; }
    // Transform scenario to match expected format, maintaining both id and _id fields
    const transformedScenario = {
      id: scenario._id || scenario.id,
      _id: scenario._id,
      name: scenario.name,
      description: scenario.description,
      status: scenario.status,
      type: scenario.type,
      lastRun: scenario.lastRun,
      createdDate: scenario.createdAt,
      runTime: scenario.runTime,
      recoveryRate: scenario.results?.recovery,
      results: scenario.results
    };
    auth.setSelectedScenario(transformedScenario);
    setScenarioDropdownOpen(false);
  };

  // Sections an admin has turned off for this project. auth.selectedProject is
  // kept current (synced on save + self-healed from the fetched list above);
  // the list is the fallback for stale pre-feature localStorage entries.
  const disabledSections = auth.selectedProject?.disabledSections !== undefined
    ? getDisabledSections(auth.selectedProject)
    : getDisabledSections(projects.find(p => (p._id || p.id) === projectId));
  const sectionDisabled = (key) => isSectionKeyHidden(disabledSections, key);
  const pageDisabled = (key) => isPageKeyHidden(disabledSections, key);

  // Drops sub-pages an admin turned off individually, hides the whole section
  // when nothing remains, and lands the section link on the first page that is
  // still visible.
  const buildSection = (sectionKey, def) => {
    if (sectionDisabled(sectionKey)) return null;
    const subPages = def.subPages.filter(p => !p.pageKey || !pageDisabled(p.pageKey));
    if (!subPages.length) return null;
    return { ...def, subPages, path: subPages[0].path };
  };

  const navigationStructure = {
    overview: {
      label: 'Overview',
      icon: 'overview',
      path: '/project-details',
      subPages: []
    },
    // Sub-category so future management features (portfolio timeline,
    // baseline reports, …) slot in alongside the task board.
    economic: buildSection('management', {
      label: 'Management',
      icon: 'management',
      subPages: [
        { label: 'Tasks & Board', icon: 'board', path: '/management', pageKey: 'management.tasks' },
        { label: 'Templates', icon: 'templates', path: '/task-templates', pageKey: 'management.templates' }
      ]
    }),
    engineering: hasProjectPermission('project.engineering.read') ? buildSection('engineering', {
      label: 'Heap Leach',
      icon: 'heapLeach',
      subPages: [
        { label: 'Heaps', icon: 'heaps', path: '/heaps', pageKey: 'engineering.heaps' },
        { label: 'Kinetics', icon: 'kinetics', path: '/kinetics', pageKey: 'engineering.kinetics' },
        { label: 'Hydrodynamics', icon: 'hydrodynamics', path: '/hydrodynamics', pageKey: 'engineering.hydrodynamics' },
        { label: 'Leaching', icon: 'leaching', path: '/leaching', pageKey: 'engineering.leaching' },
        { label: 'Run Simulation', icon: 'run', path: '/run', pageKey: 'engineering.run' },
        // Geode model editor — needs the global geode.use permission AND the
        // project page toggle, which new projects start with turned off.
        (auth.user?.role === 'admin' || auth.user?.permissions?.includes('geode.use'))
          ? { label: 'Geode Models', icon: 'templates', path: '/simulation-config', pageKey: 'engineering.geode' }
          : null,
        { label: 'Results', icon: 'results', path: '/results', pageKey: 'engineering.results' }
      ].filter(Boolean)
    }) : null,
    // Tools for developing kinetics (the lab-test library and curve fitting)
    // rather than simulation inputs, so they get their own group. Gated like
    // Heap Leach and hidden with it (see projectSections.js).
    kinetics: hasProjectPermission('project.engineering.read') ? buildSection('kinetics', {
      label: 'Kinetics Tools',
      icon: 'kineticsFitting',
      subPages: [
        { label: 'Lab Tests', icon: 'kinetics', path: '/lab-tests', pageKey: 'kinetics.labTests' },
        { label: 'Kinetics Fitting', icon: 'kineticsFitting', path: '/kinetics-fitting', pageKey: 'kinetics.fitting' }
      ]
    }) : null,
    // Site-scoped inputs shared by every facility type. Split out of the
    // Engineering (now Heap Leach) section; gated by the same permission
    // until facility sections beyond the heap exist.
    sitewater: hasProjectPermission('project.engineering.read') ? buildSection('sitewater', {
      label: 'Site & Water',
      icon: 'siteWater',
      subPages: [
        { label: 'Climate', icon: 'climate', path: '/climate', pageKey: 'sitewater.climate' },
        { label: 'Water Balance', icon: 'waterBalance', path: '/waterbalance', pageKey: 'sitewater.waterBalance' }
      ]
    }) : null,
    // Cross-cutting analysis of simulation output. Items carry their own
    // gates: NPV is financial data (project.financial.read, same gate as
    // budget fields); Ensembles configure engineering variants
    // (project.engineering.read). The section shows when ANY item does.
    analysis: buildSection('analysis', {
      label: 'Analysis & Economics',
      icon: 'analysis',
      subPages: [
        hasProjectPermission('project.financial.read')
          ? { label: 'NPV & Cashflow', icon: 'npv', path: '/analysis/npv', pageKey: 'analysis.npv' }
          : null,
        hasProjectPermission('project.engineering.read')
          ? { label: 'Ensembles', icon: 'ensembles', path: '/analysis/ensembles', pageKey: 'analysis.ensembles' }
          : null,
        // Post-leaching draindown analysis of a completed simulation —
        // engineering data, same gate as Ensembles.
        hasProjectPermission('project.engineering.read')
          ? { label: 'Closure', icon: 'closure', path: '/analysis/closure', pageKey: 'analysis.closure' }
          : null,
        // Forecast vs actuals spans results + operational data — visible to
        // engineers and to operations staff who hold operational-data read.
        hasProjectPermission('project.engineering.read') || hasProjectPermission('project.operationalData.read')
          ? { label: 'Reconciliation', icon: 'reconciliation', path: '/analysis/reconciliation', pageKey: 'analysis.reconciliation' }
          : null,
      ].filter(Boolean)
    }),
    compliance: buildSection('compliance', {
      label: 'Compliance',
      icon: 'compliance',
      subPages: [
        { label: 'Document Register', icon: 'documents', path: '/documents', pageKey: 'compliance.documents' },
        { label: 'Transmittals', icon: 'transmittals', path: '/transmittals', pageKey: 'compliance.transmittals' },
        { label: 'My Approvals', icon: 'approvals', path: '/approvals', pageKey: 'compliance.approvals' }
      ]
    }),
    operation: buildSection('operation', {
      label: 'Operation',
      icon: 'operations',
      subPages: [
        { label: 'Operational Data', icon: 'operationalData', path: '/operational-data', pageKey: 'operation.operationalData' },
        { label: 'Maintenance Logs', icon: 'maintenance', path: '/maintenance', pageKey: 'operation.maintenance' }
      ]
    })
  };

  // ── Shared render blocks ───────────────────────────────────────────
  // The desktop <aside> and the mobile drawer render the same project
  // navigation, so this markup is defined once instead of per render path.
  const selectorsBlock = (
    <>
      {/* Project Selector */}
      <div 
        className="sidebar-selector"
        onMouseEnter={handleSelectorMouseEnter}
        onMouseLeave={handleSelectorMouseLeave}
      >
        <button 
          className="sidebar-selector-button"
          onClick={toggleProjectDropdown}
        >
          <div className="selector-info">
            <span className="selector-icon">📁</span>
            <div className="selector-text">
              <span className="selector-label">Project</span>
              <span className="selector-value">{auth.selectedProject?.name || projectName || 'Select Project'}</span>
            </div>
          </div>
          <span className={`selector-arrow ${projectDropdownOpen ? 'open' : ''}`}>▼</span>
        </button>

        {projectDropdownOpen && (
          <div className="sidebar-dropdown">
            {loadingProjects ? (
              <div className="sidebar-dropdown-item" style={{ textAlign: 'center', padding: '1rem' }}>
                Loading projects...
              </div>
            ) : projects.length === 0 ? (
              <div className="sidebar-dropdown-item" style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
                No projects available
              </div>
            ) : (
              <>
                {projects.slice(0, 5).map(project => {
                  const pid = project._id || project.id;
                  const selectedId = auth.selectedProject?._id || auth.selectedProject?.id;
                  return (
                    <button
                      key={pid}
                      className={`sidebar-dropdown-item ${selectedId === pid ? 'active' : ''}`}
                      onClick={() => handleProjectSelect(project)}
                    >
                      <div className="dropdown-item-info">
                        <span className="dropdown-item-name">{project.name}</span>
                        {project.status && (
                          <span className={`dropdown-item-status status-${project.status.toLowerCase()}`}>
                            {project.status}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
                {projects.length > 5 && (
                  <button
                    className="sidebar-dropdown-item sidebar-dropdown-view-more"
                    onClick={() => { if (!confirmNavigation()) { setProjectDropdownOpen(false); return; } setProjectDropdownOpen(false); navigate('/projects'); closeMobileSidebar(); }}
                  >
                    View all projects ({projects.length})
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Scenario Selector */}
      <div 
        className="sidebar-selector"
        onMouseEnter={handleSelectorMouseEnter}
        onMouseLeave={handleSelectorMouseLeave}
      >
        <button 
          className="sidebar-selector-button"
          onClick={toggleScenarioDropdown}
        >
          <div className="selector-info">
            <span className="selector-icon">🎯</span>
            <div className="selector-text">
              <span className="selector-label">Scenario</span>
              <span className="selector-value">
                {auth.selectedScenario?.locked && (
                  <span title="This scenario is locked — read-only" aria-label="Locked">🔒 </span>
                )}
                {auth.selectedScenario?.name || 'Select Scenario'}
              </span>
            </div>
          </div>
          <span className={`selector-arrow ${scenarioDropdownOpen ? 'open' : ''}`}>▼</span>
        </button>

        {scenarioDropdownOpen && (
          <div className="sidebar-dropdown">
            {!auth.selectedProject ? (
              <div className="sidebar-dropdown-item" style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
                Select a project first
              </div>
            ) : loadingScenarios ? (
              <div className="sidebar-dropdown-item" style={{ textAlign: 'center', padding: '1rem' }}>
                Loading scenarios...
              </div>
            ) : visibleScenarios.length === 0 ? (
              <div className="sidebar-dropdown-item" style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
                No scenarios in this project
              </div>
            ) : (
              visibleScenarios.map(scenario => {
                const scenarioId = scenario._id || scenario.id;
                const selectedId = auth.selectedScenario?._id || auth.selectedScenario?.id;
                return (
                  <button
                    key={scenarioId}
                    className={`sidebar-dropdown-item ${selectedId === scenarioId ? 'active' : ''}`}
                    onClick={() => handleScenarioSelect(scenario)}
                  >
                    <div className="dropdown-item-info">
                      <span className="dropdown-item-name">
                        {scenario.locked && (
                          <span title="This scenario is locked — read-only" aria-label="Locked">🔒 </span>
                        )}
                        {scenario.name}
                      </span>
                      {scenario.status && (
                        <span className={`dropdown-item-status status-${scenario.status.toLowerCase()}`}>
                          {scenario.status}
                        </span>
                      )}
                    </div>
                    {scenario.description && (
                      <span className="dropdown-item-description">{scenario.description}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </>
  );

  const navCategoriesBlock = (
    <nav className="sidebar-nav">
      {Object.entries(navigationStructure).filter(([, category]) => category !== null).map(([key, category]) => (
        <div key={key} className="sidebar-category">
          <div className="sidebar-category-header">
            {category.disabled ? (
              <span className="sidebar-category-link disabled">
                <span className="sidebar-category-icon"><Icon name={category.icon} size={19} /></span>
                <span className="sidebar-category-label">{category.label}</span>
              </span>
            ) : (
              <NavLink 
                to={category.path}
                className={({ isActive }) => `sidebar-category-link ${isActive ? 'active' : ''}`}
                onClick={() => {
                  if (category.subPages.length > 0) {
                    setExpandedCategories(prev => ({ ...prev, [key]: true }));
                  }
                  closeMobileSidebar();
                }}
                end
              >
                <span className="sidebar-category-icon"><Icon name={category.icon} size={19} /></span>
                <span className="sidebar-category-label">{category.label}</span>
              </NavLink>
            )}

            {category.subPages.length > 0 && (
              <button
                className={`sidebar-expand-button ${expandedCategories[key] ? 'expanded' : ''}`}
                onClick={() => toggleCategory(key)}
                aria-label={`${expandedCategories[key] ? 'Collapse' : 'Expand'} ${category.label}`}
              >
                <span className="expand-arrow">▼</span>
              </button>
            )}
          </div>

          {/* Sub Pages */}
          {category.subPages.length > 0 && (
            <div className={`sidebar-subpages ${expandedCategories[key] ? 'expanded' : ''}`}>
              {category.subPages.map((subPage, index) => (
                <NavLink
                  key={index}
                  to={subPage.path}
                  className={({ isActive }) => `sidebar-subpage-link ${isActive ? 'active' : ''}`}
                  onClick={closeMobileSidebar}
                >
                  <span className="sidebar-subpage-icon"><Icon name={subPage.icon} size={17} /></span>
                  <span className="sidebar-subpage-label">{subPage.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );

  const helpLinkBlock = (
    <NavLink
      to="/help"
      state={{ from: location.pathname + location.search }}
      className={({ isActive }) => `sidebar-help-link ${isActive ? 'active' : ''}`}
      onClick={closeMobileSidebar}
    >
      <span className="sidebar-help-icon" aria-hidden="true">❓</span>
      <span>Help &amp; Documentation</span>
    </NavLink>
  );

  if (!isVisible) return null;

  // Mobile: the project navigation is folded into the global side drawer so the
  // single ☰ in the header opens everything. It previously lived behind its own
  // screen-edge tab, which sat inside the browser's back-swipe gesture zone and
  // was effectively untappable. Clicks are stopped here because SideDrawer closes
  // on any click that reaches it — only real navigation should dismiss it, not
  // opening a selector or expanding a category.
  // Mobile has no standalone sidebar: MainNavigation renders the `embedded`
  // form above into the global side drawer, so one header ☰ opens everything.
  // The old screen-edge pull tab is gone with it — it sat inside the browser's
  // back-swipe gesture zone and could not be tapped reliably.
  if (isMobile && !embedded) return null;

  if (embedded) {
    return (
      <div className="drawer-project-nav" onClick={(e) => e.stopPropagation()}>
        {/* No project-name heading here: the Project selector immediately below
            already names it, and is tappable to switch. */}
        <div className="drawer-project-selectors">{selectorsBlock}</div>
        {navCategoriesBlock}
        {/* No help link here — the drawer's own "Help Center" entry below the
            global links goes to the same place. */}
      </div>
    );
  }

  return (
    <>
      <SlotMachineModal
        show={showSlotMachine}
        onClose={() => setShowSlotMachine(false)}
      />
      {/* Hover trigger strip (this render path is desktop-only) */}
      <div
        className="sidebar-trigger"
        onMouseEnter={handleTriggerMouseEnter}
      />

      {/* Sidebar */}
      <aside 
        className={`project-sidebar ${sidebarVisible ? 'visible' : ''} ${isPinned ? 'pinned' : ''}`}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="sidebar-header-top">
            <div className="sidebar-title" onClick={handleTitleClick}>Orecasting</div>
            <button
              className={`sidebar-pin-button ${isPinned ? 'pinned' : ''}`}
              onClick={togglePin}
              title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
            >
              📌
            </button>
          </div>
          
          {selectorsBlock}
        </div>

        {/* Navigation Categories */}
        {navCategoriesBlock}

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          {helpLinkBlock}
          <div className="sidebar-hint">
            {isPinned ? 'Sidebar pinned' : 'Hover to keep open'}
          </div>
        </div>
      </aside>

      {/* Backdrop — shown while the sidebar is open but unpinned */}
      {!isPinned && sidebarVisible && (
        <div
          className="sidebar-backdrop"
          onClick={() => setIsHovered(false)}
        />
      )}
    </>
  );
};

export default React.memo(ProjectSidebar);
